// Typed accessor for the desktop shell's embedded-terminal bridge
// (packages/desktop-cyborg/preload.cts `cyborg7Desktop.setupTerminal`).
// Returns null in a plain browser or on a desktop build that predates the
// bridge — callers degrade to showing the command to run.

import { reportUiFailure } from "$lib/report-failure.js";

export interface DesktopTerminalBridge {
  hostInfo(): Promise<{ hostname: string; platform: string }>;
  start(opts: { cols: number; rows: number; command?: string }): Promise<{ id: string }>;
  input(id: string, data: string): Promise<void>;
  resize(id: string, cols: number, rows: number): Promise<void>;
  kill(id: string): Promise<void>;
  onData(handler: (payload: { id: string; data: string }) => void): () => void;
  onExit(handler: (payload: { id: string; exitCode: number }) => void): () => void;
}

export function desktopTerminalBridge(): DesktopTerminalBridge | null {
  if (typeof window === "undefined") return null;
  const bridge = window.cyborg7Desktop?.setupTerminal;
  return (bridge as DesktopTerminalBridge | undefined) ?? null;
}

/** The Mac sign-in window (preload `authSession`); null in a browser or on an older shell. */
export function desktopAuthSession(): Cyborg7DesktopAuthSession | null {
  if (typeof window === "undefined") return null; // null: no window (SSR)
  return window.cyborg7Desktop?.authSession ?? null; // null: a browser, or an older shell
}

/** Bring the Cyborg window to the front; a no-op without the bridge. */
export function focusMainWindow(): void {
  const focus = typeof window === "undefined" ? undefined : window.cyborg7Desktop?.focusMainWindow;
  focus?.().catch((e: unknown) => reportUiFailure("connect.focus_main_window", e));
}

/** The OS handed the app cyborg7://connect-done; returns the unsubscribe (a no-op without it). */
export function onDesktopConnectDone(cb: () => void): () => void {
  const on = typeof window === "undefined" ? undefined : window.cyborg7Desktop?.onConnectDone;
  return on ? on(() => cb()) : () => {};
}

// Open an http(s) URL in the OS browser, NOT inside the app WebView (which would
// destroy the shell). Prefers the desktop shell's `openExternal` bridge when the
// build exposes it; degrades to window.open with noopener in a plain browser.
// Used by the provider-remedy "open_url" action (Add usage, etc.).
export function openExternalUrl(url: string): void {
  if (typeof window === "undefined") return;
  const bridge = window.cyborg7Desktop?.openExternal;
  if (typeof bridge === "function") {
    bridge(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * "Open in Terminal" for an agent's staged command, or undefined when it cannot apply:
 * no command, not the macOS desktop app, an older shell, or a terminal on another
 * machine (Terminal.app would run it on THIS Mac). Resolves an error message on failure.
 */
export function terminalAppOpener(input: {
  commandLine: string | undefined;
  cwd: string | undefined;
  daemonId: string | null | undefined;
  localDaemonId: string | null | undefined;
}): (() => Promise<string | undefined>) | undefined {
  const bridge = typeof window === "undefined" ? undefined : window.cyborg7Desktop;
  const open = bridge?.openInTerminalApp;
  const { commandLine, cwd, daemonId } = input;
  const local = Boolean(daemonId) && input.localDaemonId === daemonId;
  if (!commandLine || !local || bridge?.platform !== "darwin" || !open) return undefined;
  return async () => {
    try {
      const res = await open({ commandLine, ...(cwd ? { cwd } : {}) });
      return res.ok ? undefined : res.error;
    } catch (err) {
      reportUiFailure("terminal.open_in_terminal_app", err);
      return "Terminal could not be opened";
    }
  };
}
